import { RouteObject } from "react-router";
import { CmsDynamicRoute, CmsDynamicRoutesExtract, MenuItem, MenuItemWithSubs, MenuTree } from "@shared/routing";
import { ArticleType } from "@shared/url";
import { allLocales } from "@shared/locale";
import { extractOne } from "@shared/utils";

// helper:
//  - important: staticRoutes must be a function to prevent SSR memoized routes context between requests
//  - compId: simulate react element to be able to handle router context between ssr and web
//            any react related data (element, asMenu) must be applied later into /src/app/routes.tsx

export const staticRoutes = (): RouteObject[] => [
  {
    compId: "home",
    index: true,
    loader: () => ({ page: { headerOverlap: true } })
  },
  {
    path: "sales",
    children: [
      {
        compId: "search-sale",
        path: "properties",
        index: true,
        loader: () => ({ search: { withSearchParams: ArticleType.SALE, targeted: true } })
      },
      {
        path: "property-development"
      },
      {
        compId: "article",
        path: "properties/:slug",
        loader: () => ({ article: { type: ArticleType.SALE } })
      },
      {
        compId: "search-sold",
        path: "history",
        index: true,
        loader: () => ({ search: { withSearchParams: "sold", targeted: true } })
      }
    ]
  },
  {
    path: "rentals",
    children: [
      {
        compId: "search-rental",
        path: "properties",
        index: true,
        loader: () => ({ search: { withSearchParams: ArticleType.RENTAL, targeted: true } })
      },
      {
        compId: "article",
        path: "properties/:slug",
        loader: () => ({ article: { type: ArticleType.RENTAL } })
      }
    ]
  },
  {
    path: "renovation",
    children: [
      {
        compId: "makeovers",
        path: "makeovers",
        index: true,
        loader: () => ({ page: { headerOverlap: true } })
      },
      {
        compId: "makeover",
        path: "makeovers/:id"
      }
    ]
  },
  {
    path: "services"
  },
  {
    path: "agency",
    children: [
      {
        compId: "news-index",
        path: "news",
        loader: () => ({ page: { headerOverlap: true } })
      },
      {
        compId: "news-item",
        path: "news/:id",
        loader: () => ({ page: { headerOverlap: true } })
      }
    ]
  },
  {
    compId: "contact",
    path: "contact",
    index: true,
    loader: () => ({ page: { headerOverlap: true } })
  },
  {
    path: "with-us",
    children: [
      {
        compId: "estimate",
        path: "estimate",
        loader: () => ({ page: { headerOverlap: true } })
      }
    ]
  },
  {
    compId: "alert",
    path: "alert",
    index: true,
    loader: () => ({ page: { headerOverlap: true } })
  },
  {
    path: "checkout",
    children: [
      {
        index: true,
        compId: "checkout"
      },
      {
        path: "success",
        compId: "payment-callback"
      },
      {
        path: "cancellation",
        compId: "payment-callback"
      }
    ]
  },
  {
    compId: "bookmarks",
    path: "bookmarks",
    index: true,
    loader: () => ({ page: { headerOverlap: true } })
  },
  {
    compId: "not-found",
    path: "*"
  }
];


export const mergedRoutes = (dynamics: CmsDynamicRoutesExtract):
  { router: RouteObject[], menuable: RouteObject[] } => {

  const all = [ ...staticRoutes() ];
  const { extracted: promos, rest: main } = extractOne<CmsDynamicRoute[], CmsDynamicRoutesExtract>(dynamics,
    (_, key) => key === "property-development");
  Object.entries(main).forEach(([ path, content ]) => {
    const searchIndex = all.findIndex(item => !!item.path && item.path === path);
    const dynamicIndex = content.find(dc => !("path" in dc));
    if (searchIndex >= 0) {
      ("element" in all[searchIndex] && !!dynamicIndex) && delete all[searchIndex];
      const { extracted, rest: children } = extractOne<RouteObject>(all[searchIndex].children || [],
          route => route.path === "property-development");
      all[searchIndex].children = [
        ...(children as RouteObject[]).filter(sc => !dynamicIndex || "path" in sc),
        ...(path !== "sales" || !extracted ? [] : [ {
          path: "property-development",
          children: [
            ...(extracted!.children || []),
            ...(!!promos ? generateChildrenRoutes(promos, "property-development") : [] )
          ]
        } ]),
        ...generateChildrenRoutes(content, path),
      ];
    }
    else {
      all.push({
        path,
        children: generateChildrenRoutes(content, path)
      });
    }
  });

  const menuable = all.filter(child => !!child.compId || (child.children || []).some(cc => cc.compId));
  return {
    router: [ { compId: "app", children: allLocales.map(({ basename: path }) => ({ path, children: menuable })) } ],
    menuable
  }

}

const generateChildrenRoutes = (content: CmsDynamicRoute[], category: string): RouteObject[] =>
  content.map(({ cmsData: { label: asMenu, ...data }, ...route }) => ({
    compId: "default-cms",
    ...route,
    loader: () => ({
      page: { headerOverlap: true },
      cms: { ...data, category, isIndex: !("path" in route) }
    }),
    asMenu
  }));

export const buildMenuTree = (router: RouteObject[]): MenuTree => router.reduce<MenuTree>((acc, cur) => {
  const mainItem = recursiveTree(cur);
  !!mainItem && acc.push(mainItem);
  return acc;
}, []);

const recursiveTree = (route: RouteObject, parentPath?: string): MenuItem | MenuItemWithSubs | undefined => {
  const { extracted: indexChild, rest } = extractOne<RouteObject>(route.children || [], child =>
    !child.path && !!child.index && !!child.asMenu);

  const path = `${parentPath || "/"}${route.path}/`;
  const subs = rest.map(child => recursiveTree(child, path)).filter(res => !!res);

  if (subs.length === 0 && !!indexChild)
    return {
      label: "label" in indexChild.asMenu! ? indexChild.asMenu.label : indexChild.asMenu,
      path
    } as MenuItem;

  if (subs.length > 0)
    return {
      label: "label" in route.asMenu! ? route.asMenu.label : route.asMenu,
      ...("order" in route.asMenu!) && { order: route.asMenu.order },
      subs: [ ...(!!indexChild ? [ { path, label: indexChild.asMenu } ]: []), ...subs ]
    } as MenuItemWithSubs;

  if (!!route.path && !!route.asMenu && !!route.element)
    return {
      label: "label" in route.asMenu! ? route.asMenu.label : route.asMenu,
      ...("order" in route.asMenu!) && { order: route.asMenu.order },
      path
    } as MenuItem;

  return undefined;
}
